import React from 'react';
import MarketingLayout from '../components/MarketingLayout';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar, Chip } from '@mui/material';
import { aboutContent } from '../data/aboutContent';

const About = () => {
  const { hero, stats, mission, founders, services, values, contact, press } = aboutContent;

  return (
    <MarketingLayout>
    <Box sx={{ backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            {hero.title}
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            {hero.subtitle}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.9 }}>
            {hero.description}
          </Typography>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} sx={{ textAlign: 'center' }}>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {stats.aum}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Assets Under Management
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {stats.exits}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Successful Exits
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {stats.members}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Active Members
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {stats.companies}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Portfolio Companies
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Mission Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
            {mission.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 900, mx: 'auto', whiteSpace: 'pre-line' }}
          >
            {mission.content}
          </Typography>
        </Container>
      </Box>

      {/* Founders Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
          Our Founders
        </Typography>
        <Grid container spacing={4}>
          {founders.map((founder, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, height: '100%' }}>
                <Avatar src={founder.image} alt={founder.name} sx={{ width: 150, height: 150, mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {founder.name}
                </Typography>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {founder.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                  {founder.bio}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
            Our Services
          </Typography>
          <Grid container spacing={4}>
            {services.map((service) => (
              <Grid item xs={12} md={4} key={service.id}>
                <Card sx={{ height: '100%', p: 3 }}>
                  <Box sx={{ fontSize: 48, textAlign: 'center', mb: 2 }}>{service.icon}</Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {service.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                    {service.description}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {service.features.map((feature, idx) => (
                      <Typography component="li" variant="body2" key={idx} sx={{ mb: 1 }}>
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
          Our Values
        </Typography>
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#478289' }}>
                  {value.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {value.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Press Mentions */}
      <Box sx={{ backgroundColor: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 4, color: 'textSecondary' }}>
            As Featured In
          </Typography>
          <Grid container spacing={4} sx={{ justifyContent: 'center', alignItems: 'center' }}>
            {press.map((outlet, index) => (
              <Grid item key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4">{outlet.logo}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {outlet.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{ backgroundColor: '#052d50', color: 'white', py: 6 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Get In Touch
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {contact.address}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {contact.phone}
            </Typography>
            <Typography variant="body1">{contact.email}</Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  
    </MarketingLayout>
  );
};

export default About;
